import React, { FC, useEffect, useState } from "react";
import ActivityFrame from "../../activity-frame/activity-frame";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import TableTab from "../../tabs/table-tab";
import { ListQuery } from "../../../models/ListQuery";
import { ListData } from "../../../models/ListData";
import { appendFilter } from "../../../utilities/filters";
import { getEventTypeNamesWithPagination, getEventTypes } from "../../../services/eventTypeService";
import { useLocale } from "../../../contexts/localeContext";
import EventTypeColumns from "../../../models/ColumnDefinitions/EventTypeColumns";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import { DEF_PG_SIZE_WIZARD } from "../../../constants/maximum-page-size";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from '../../../components/button/Button';
import { useFormikContext } from "formik";
import { FILTER_EVENTS } from "../../../constants/notification-key";
import {  handleManage } from "../../../services/notificationService";
import { toast } from "react-toastify";
import { useDialog } from "../../dialog/DialogContext";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { SettingData } from "../../../models/SettingData";
import AssignModalTwo from "@/components/assignWizard/AssignModal";
import { PaginationModel } from "@/models/Pagination";


interface EventDisplayProps {
    reloadPage: () => void;
    currentItem: any;
    id: string;
}
const EventDisplay: FC<EventDisplayProps> = ({ reloadPage, currentItem, id }) => {
    const [busy, setBusy] = useState(false);
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    const [showManagedModal, setShowManagedModal] = useState(false);
    const dialog = useDialog();
    let settingDataToRemove: string[] = [];

    const getDataMethod = async (listParameters?: ListQuery) => {
        let data = await getEventTypes(translate, {
            ...listParameters,
            filters: appendFilter(
                currentItem.filterEvents?.toString(),
                "eventTypeId",
                "id",
                listParameters?.filters ?? [],
                "=in=")
        })
        return data;
    }

    useEffect(() => {
        dataRefresh((listParameters?: ListQuery) => getDataMethod(listParameters));
    }, [currentItem.filterEvents])

    const handleSubmit = async (toBeAdded: string[], toBeRmoved: string[]) => {
        setBusy(true);
        settingDataToRemove = [];
        const settingDataToAdd:SettingData[] =  toBeAdded.map((id) => ({
            sdValstring: id,
            eSysnameLookup: FILTER_EVENTS,
            relationship:[
                {
                    id: values.vruleTriggerEventId,
                    type: "ruleTriggerGroup",
                    relationType:"vruleTriggerGroup"
                },
                {
                    id: id,
                    type: "eventType",
                }
            ]
        }))

        if (toBeRmoved.length > 0) {
            const filteredOutSettingData = values.settingDatasRef.filter((ref) => ref.elementSysname === FILTER_EVENTS && toBeRmoved.includes(ref.sdValstring));
            filteredOutSettingData.map((remove) => {
                settingDataToRemove.push(remove.id)
            })
        }

        handleManage(settingDataToAdd, settingDataToRemove).then(() => {
            toast.success(translate.byKey(TranslationKeys.recordUpdated));
            reloadPage();
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        }).finally(() => {
            setBusy(false);
        })
    }

    const getAssignedEvents = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];

            do {
                const events = await getEventTypeNamesWithPagination({ filters: appendFilter(currentItem.filterEvents?.toString(), "eventTypeId", "id", [], "=in="), pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(events.rows.map(event => event.value));
                rows = rows.concat(events.rows.map(row => ({id: row.value, name: row.label})));
                pagination = events.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        } finally {
            setBusyLoader(false);
        }
    };

    return (<>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <div className="w-full flex flex-col">
                <TableContextProvider
                    dataMethod={getDataMethod}
                    useTable
                    usePagination
                    useFilter
                    useSearch
                    useSort
                    tableDescriptor={EventTypeColumns}
                    searchDescriptor={EventTypeSearch}
                    maxPageSize={DEF_PG_SIZE_WIZARD}
                    manualDataRefresh={manualDataRefresh}
                    canExportReport={false}>
                    <TableTab
                        title={translate.byKey(TranslationKeys.events)}
                        selectRows={false}
                        tableId={'eventBasedTable'}
                        cyName="eventDisplay">
                        <Button variant="submit" shape="rounded" className="py-1 px-5"
                            onClick={() => setShowManagedModal(true)}>
                            {translate.byKey(TranslationKeys.manage)}
                        </Button>
                    </TableTab>
                </TableContextProvider>
            </div>
            <AssignModalTwo
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                assigningTitle={translate.byKey(TranslationKeys.events)}
                modalTitle={translate.byKey(TranslationKeys.assignEvents)}
                dataMethod={(listParameters) => getEventTypes(translate,listParameters)}
                lookup={[]}
                tableColumns={EventTypeColumns}
                searchColumns={EventTypeSearch}
                getAssignedItems={getAssignedEvents}
                handleSubmit={handleSubmit}/>
        </ActivityFrame>
    </>)
}
export default EventDisplay;